// src/components/settings/FormAddEditSettings.jsx
import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Btn from '../global/Btn';
import { BoxWrap } from '../UI/BoxWrap';
import { useUpdateSettings } from '../../hooks/Settings/useUpdateSettings';
import { useUploadMedia } from '../../hooks/media/useUploadMedia';
import { useGetSettings } from '../../hooks/Settings/useGetSettings';
import i18next from 'i18next';
import LanguageToggle from '../global/LanguageToggle';

import BrandingSection from './sections/BrandingSection';
import CompanyInfoSection from './sections/CompanyInfoSection';
import ContentFooterSection from './sections/ContentFooterSection';
import ContactDetailsSection from './sections/ContactDetailsSection';
import SocialLinksSection from './sections/SocialLinksSection';

const defaultFormData = {
    name_ar: '',
    name_en: '',
    main_location_ar: '',
    main_location_en: '',
    address1_ar: '',
    address1_en: '',
    address2_ar: '',
    address2_en: '',
    contact_us_ar: '',
    contact_us_en: '',
    footer_bio_ar: '',
    footer_bio_en: '',
    tel1: '',
    tel2: '',
    email: '',
    whatsapp_number: '',
    twitter_url: '',
    instagram_url: '',
    linkedin_url: '',
    facebook_url: '',
    tiktok_url: '',
    snapchat_url: '',
    logo_name: '',
    footer_name: '',
    fav_icon_name: '',
    logo_preview: '',
    footer_preview: '',
    fav_icon_preview: '',
};

const FormAddEditSettings = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const { editSettings, isUpdate } = useUpdateSettings();
    const { Settings, isLoading } = useGetSettings();
    const { uploadMedia } = useUploadMedia();

    const [formData, setFormData] = useState(defaultFormData);
    const [expanded, setExpanded] = useState('branding');

    const initialLang = localStorage.getItem('i18nextLng') || i18next.language || 'ar';
    const [activeLang, setActiveLang] = useState(initialLang.startsWith('ar') ? 'ar' : 'en');

    const showArabic = activeLang === 'ar';
    const showEnglish = activeLang === 'en';

    // Branding states
    const [logoError, setLogoError] = useState('');
    const [footerError, setFooterError] = useState('');
    const [faviconError, setFaviconError] = useState('');
    const [logoUploadedName, setLogoUploadedName] = useState('');
    const [footerUploadedName, setFooterUploadedName] = useState('');
    const [faviconUploadedName, setFaviconUploadedName] = useState('');
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    const [isUploadingFooter, setIsUploadingFooter] = useState(false);
    const [isUploadingFavicon, setIsUploadingFavicon] = useState(false);

    // Errors state
    const [errors, setErrors] = useState({
        company: false,
        contact: false,
        footer: false,
        branding: false,
        social: false
    });

    // Validation function
    const validateSection = (section) => {
        switch (section) {
            case 'company':
                // تحقق بناءً على اللغة النشطة فقط
                if (showArabic && !formData.name_ar?.trim()) return true;
                if (showEnglish && !formData.name_en?.trim()) return true;
                return false;
            case 'contact':
                return !formData.email?.trim() && !formData.tel1?.trim();
            case 'footer':
                if (showArabic && !formData.footer_bio_ar?.trim()) return true;
                if (showEnglish && !formData.footer_bio_en?.trim()) return true;
                return false;
            case 'branding':
                return !formData.logo_name && !logoUploadedName;
            default:
                return false;
        }
    };

    const handleAccordionChange = (panel) => (_, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    useEffect(() => {
        if (!isLoading && Settings?.data) {
            const s = Settings.data;
            setFormData({
                name_ar: s.name?.ar || '',
                name_en: s.name?.en || '',
                main_location_ar: s.main_location?.ar || '',
                main_location_en: s.main_location?.en || '',
                address1_ar: s.address1?.ar || '',
                address1_en: s.address1?.en || '',
                address2_ar: s.address2?.ar || '',
                address2_en: s.address2?.en || '',
                contact_us_ar: s.contact_us?.ar || '',
                contact_us_en: s.contact_us?.en || '',
                footer_bio_ar: s.footer_bio?.ar || '',
                footer_bio_en: s.footer_bio?.en || '',
                tel1: s.tel1 || '',
                tel2: s.tel2 || '',
                email: s.email || '',
                whatsapp_number: s.whatsapp_number || '',
                twitter_url: s.twitter_url || '',
                instagram_url: s.instagram_url || '',
                linkedin_url: s.linkedin_url || '',
                facebook_url: s.facebook_url || '',
                tiktok_url: s.tiktok_url || '',
                snapchat_url: s.snapchat_url || '',
                logo_name: s.logo?.file_name || '',
                footer_name: s.footer?.file_name || '',
                fav_icon_name: s.favicon?.file_name || '',
                logo_preview: s.logo?.url || s.logo?.preview || '',
                footer_preview: s.footer?.url || s.footer?.preview || '',
                fav_icon_preview: s.favicon?.url || s.favicon?.preview || '',
            });
            setLogoUploadedName(s.logo?.file_name || '');
            setFooterUploadedName(s.footer?.file_name || '');
            setFaviconUploadedName(s.favicon?.file_name || '');
        }
    }, [Settings, isLoading]);

    const handleTextChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        // إزالة الخطأ عند التعديل
        if (errors.company) {
            setErrors(prev => ({ ...prev, company: false }));
        }
    };

    const prepareData = () => ({
        name: { ar: formData.name_ar, en: formData.name_en },
        main_location: { ar: formData.main_location_ar, en: formData.main_location_en },
        address1: { ar: formData.address1_ar, en: formData.address1_en },
        address2: { ar: formData.address2_ar, en: formData.address2_en },
        contact_us: { ar: formData.contact_us_ar, en: formData.contact_us_en },
        footer_bio: { ar: formData.footer_bio_ar, en: formData.footer_bio_en },
        tel1: formData.tel1,
        tel2: formData.tel2,
        email: formData.email,
        whatsapp_number: formData.whatsapp_number,
        twitter_url: formData.twitter_url,
        instagram_url: formData.instagram_url,
        linkedin_url: formData.linkedin_url,
        facebook_url: formData.facebook_url,
        tiktok_url: formData.tiktok_url,
        snapchat_url: formData.snapchat_url,
        logo: formData.logo_name || undefined,
        footer: formData.footer_name || undefined,
        fav_icon: formData.fav_icon_name || undefined,
    });

    // في FormAddEditSettings.jsx
    const SubmitForm = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const companyError = validateSection('company');
        const newErrors = {
            company: companyError,
            contact: false,
            footer: false,
            branding: false,
            social: false
        };

        setErrors(newErrors);

        if (Object.values(newErrors).some(hasError => hasError)) {
            const firstErrorSection = Object.keys(newErrors).find(key => newErrors[key]);
            setExpanded(firstErrorSection);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        editSettings({ req: prepareData() });
    };



    return (
        <BoxWrap>
            <Box
                component="form"
                onSubmit={SubmitForm}
                noValidate
                sx={{
                    p: { xs: 2, md: 4 },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography variant="h5" fontWeight={700} mb={0.5}>
                            Company Settings
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage company info, contact details, social links and branding.
                        </Typography>
                    </Box>
                    <LanguageToggle
                        activeLang={activeLang}
                        onChange={(lng) => setActiveLang(lng)}
                    />
                </Box>

                <BrandingSection
                    expanded={expanded}
                    handleAccordionChange={handleAccordionChange}
                    isDark={isDark}
                    formData={formData}
                    setFormData={setFormData}
                    uploadMedia={uploadMedia}
                    logoError={logoError}
                    setLogoError={setLogoError}
                    footerError={footerError}
                    setFooterError={setFooterError}
                    faviconError={faviconError}
                    setFaviconError={setFaviconError}
                    logoUploadedName={logoUploadedName}
                    setLogoUploadedName={setLogoUploadedName}
                    footerUploadedName={footerUploadedName}
                    setFooterUploadedName={setFooterUploadedName}
                    faviconUploadedName={faviconUploadedName}
                    setFaviconUploadedName={setFaviconUploadedName}
                    isUploadingLogo={isUploadingLogo}
                    setIsUploadingLogo={setIsUploadingLogo}
                    isUploadingFooter={isUploadingFooter}
                    setIsUploadingFooter={setIsUploadingFooter}
                    isUploadingFavicon={isUploadingFavicon}
                    setIsUploadingFavicon={setIsUploadingFavicon}
                />

                <CompanyInfoSection
                    expanded={expanded}
                    handleAccordionChange={handleAccordionChange}
                    isDark={isDark}
                    formData={formData}
                    handleTextChange={handleTextChange}
                    showArabic={showArabic}
                    showEnglish={showEnglish}
                    hasError={errors.company}
                />

                <ContentFooterSection
                    expanded={expanded}
                    handleAccordionChange={handleAccordionChange}
                    isDark={isDark}
                    formData={formData}
                    handleTextChange={handleTextChange}
                    showArabic={showArabic}
                    showEnglish={showEnglish}
                />

                <ContactDetailsSection
                    expanded={expanded}
                    handleAccordionChange={handleAccordionChange}
                    isDark={isDark}
                    formData={formData}
                    handleTextChange={handleTextChange}
                />

                <SocialLinksSection
                    expanded={expanded}
                    handleAccordionChange={handleAccordionChange}
                    isDark={isDark}
                    formData={formData}
                    handleTextChange={handleTextChange}
                />

                <Box textAlign="center" mt={1}>
                    <Btn type="submit" width="500px" widthMob="90%" disabled={isUpdate}>
                        {Settings?.data ? 'Save Changes' : 'Save Settings'}
                    </Btn>
                </Box>
            </Box>
        </BoxWrap>
    );
};

export default FormAddEditSettings;
